import { NodeViewWrapper, NodeViewProps } from "@tiptap/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { ToolbarButton } from "../texteditor";
import TextAlignLeftIcon from "lucide-react/dist/esm/icons/align-left";
import TextAlignCenterIcon from "lucide-react/dist/esm/icons/align-center";
import TextAlignRightIcon from "lucide-react/dist/esm/icons/align-right";
import TrashIcon from "lucide-react/dist/esm/icons/trash-2";

interface ResizeHandleProps {
  onResize: (width: number, height: number) => void;
  onResizeEnd: (width: number, height: number) => void;
  initialWidth: number;
  initialHeight: number;
}

const ResizeHandle = ({ onResize, onResizeEnd, initialWidth, initialHeight }: ResizeHandleProps) => {
  const isResizingRef = useRef(false);
  const startPosRef = useRef({ x: 0, y: 0 });
  const startSizeRef = useRef({ width: 0, height: 0 });
  const latestSizeRef = useRef({ width: initialWidth, height: initialHeight });

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizingRef.current) return;

      const deltaX = e.clientX - startPosRef.current.x;
      const deltaY = e.clientY - startPosRef.current.y;

      const newWidth = Math.max(50, startSizeRef.current.width + deltaX);
      const newHeight = Math.max(50, startSizeRef.current.height + deltaY);

      latestSizeRef.current = { width: newWidth, height: newHeight };
      onResize(newWidth, newHeight);
    },
    [onResize],
  );

  const handleMouseUp = useCallback(() => {
    isResizingRef.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    onResizeEnd(latestSizeRef.current.width, latestSizeRef.current.height);
  }, [handleMouseMove, onResizeEnd]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      isResizingRef.current = true;
      startPosRef.current = { x: e.clientX, y: e.clientY };
      startSizeRef.current = { width: initialWidth, height: initialHeight };
      latestSizeRef.current = { width: initialWidth, height: initialHeight };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [initialWidth, initialHeight, handleMouseMove, handleMouseUp],
  );

  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return (
    <div
      className="absolute bottom-0 right-0 w-3 h-3 bg-blue-500 cursor-se-resize opacity-0 group-hover:opacity-100 transition-opacity"
      onMouseDown={handleMouseDown}
      style={{
        transform: "translate(50%, 50%)",
      }}
    />
  );
};

export const MarkdownImageNodeView = ({ node, updateAttributes, deleteNode, editor }: NodeViewProps) => {
  const editable = editor.isEditable;
  const imgRef = useRef<HTMLImageElement>(null);
  const sizeIndicatorRef = useRef<HTMLDivElement>(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [currentSize, setCurrentSize] = useState({
    width: node.attrs.width ? parseInt(node.attrs.width.replace("px", "")) : 0,
    height: node.attrs.height ? parseInt(node.attrs.height.replace("px", "")) : 0,
  });
  const liveFrameRef = useRef<number | undefined>(undefined);
  const pendingSizeRef = useRef<{ width: number; height: number } | null>(null);

  const handleImageLoad = useCallback(() => {
    if (imgRef.current) {
      const { naturalWidth, naturalHeight } = imgRef.current;
      setNaturalSize({ width: naturalWidth, height: naturalHeight });

      // If no size is set, use natural size up to a maximum
      if (!node.attrs.width && !node.attrs.height) {
        const maxWidth = 600;
        const ratio = naturalHeight / naturalWidth;
        const width = Math.min(naturalWidth, maxWidth);
        const height = width * ratio;

        setCurrentSize({ width, height });
        updateAttributes({
          width: `${width}px`,
          height: `${height}px`,
        });
      }
    }
  }, [node.attrs.width, node.attrs.height, updateAttributes]);

  const applyLiveSize = useCallback((width: number, height: number) => {
    if (imgRef.current) {
      imgRef.current.style.width = `${width}px`;
      imgRef.current.style.height = `${height}px`;
    }

    if (sizeIndicatorRef.current) {
      sizeIndicatorRef.current.textContent = `${Math.round(width)}×${Math.round(height)}`;
    }
  }, []);

  const handleLiveResize = useCallback(
    (width: number, height: number) => {
      pendingSizeRef.current = { width, height };

      if (liveFrameRef.current) return;

      liveFrameRef.current = requestAnimationFrame(() => {
        liveFrameRef.current = undefined;

        if (!pendingSizeRef.current) return;

        applyLiveSize(pendingSizeRef.current.width, pendingSizeRef.current.height);
      });
    },
    [applyLiveSize],
  );

  const handleResizeEnd = useCallback(
    (width: number, height: number) => {
      pendingSizeRef.current = null;

      if (liveFrameRef.current) {
        cancelAnimationFrame(liveFrameRef.current);
        liveFrameRef.current = undefined;
      }

      setCurrentSize({ width, height });
      updateAttributes({
        width: `${width}px`,
        height: `${height}px`,
      });
    },
    [updateAttributes],
  );

  const setAlignment = useCallback(
    (alignment: "left" | "center" | "right") => {
      updateAttributes({ alignment });
    },
    [updateAttributes],
  );

  const isActive = useCallback(
    (alignment: "left" | "center" | "right") => {
      return node.attrs.alignment === alignment;
    },
    [node.attrs.alignment],
  );

  const handleDelete = useCallback(() => {
    deleteNode();
  }, [deleteNode]);

  useEffect(() => {
    if (node.attrs.width && node.attrs.height) {
      setCurrentSize({
        width: parseInt(node.attrs.width.replace("px", "")),
        height: parseInt(node.attrs.height.replace("px", "")),
      });
    }
  }, [node.attrs.width, node.attrs.height]);

  useEffect(() => {
    return () => {
      if (liveFrameRef.current) {
        cancelAnimationFrame(liveFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!imgRef.current) return;

    if (node.attrs.style) {
      imgRef.current.setAttribute("style", node.attrs.style);
    } else {
      imgRef.current.removeAttribute("style");
    }
  }, [node.attrs.style]);

  useEffect(() => {
    if (!imgRef.current) return;

    if (currentSize.width) {
      imgRef.current.style.width = `${currentSize.width}px`;
    } else {
      imgRef.current.style.removeProperty("width");
    }

    if (currentSize.height) {
      imgRef.current.style.height = `${currentSize.height}px`;
    } else {
      imgRef.current.style.removeProperty("height");
    }
    imgRef.current.style.display = "inline";
  }, [currentSize.height, currentSize.width]);

  const imageEl = (
    <div
      className={`relative inline-block markdown-image-container align-${node.attrs.alignment || "left"} ${editable ? "group cursor-pointer" : ""}`}
      style={{
        textAlign:
          node.attrs.alignment === "center" ? "center" : node.attrs.alignment === "right" ? "right" : "left",
      }}
    >
      <img
        ref={imgRef}
        src={node.attrs.src}
        alt={node.attrs.alt || ""}
        title={node.attrs.title || ""}
        onLoad={handleImageLoad}
        className={`max-w-full h-auto border-2 border-transparent transition-colors ${editable ? "group-hover:border-blue-300" : ""}`}
        draggable={false}
      />

      {editable && naturalSize.width > 0 && (
        <ResizeHandle
          onResize={handleLiveResize}
          onResizeEnd={handleResizeEnd}
          initialWidth={currentSize.width || naturalSize.width}
          initialHeight={currentSize.height || naturalSize.height}
        />
      )}

      {editable && (
        <div
          ref={sizeIndicatorRef}
          className="absolute top-0 left-0 bg-black bg-opacity-75 text-white text-xs px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          {currentSize.width}×{currentSize.height}
        </div>
      )}
    </div>
  );

  return (
    <NodeViewWrapper className="markdown-image-wrapper">
      {editable ? (
        <Popover>
          <PopoverTrigger asChild>{imageEl}</PopoverTrigger>
          <PopoverContent className="w-auto p-2">
            <div className="flex items-center space-x-2">
              <ToolbarButton isActive={isActive("left")} onClick={() => setAlignment("left")} tooltip="Align Left">
                <TextAlignLeftIcon className="w-4 h-4" />
              </ToolbarButton>
              <ToolbarButton isActive={isActive("center")} onClick={() => setAlignment("center")} tooltip="Align Center">
                <TextAlignCenterIcon className="w-4 h-4" />
              </ToolbarButton>
              <ToolbarButton isActive={isActive("right")} onClick={() => setAlignment("right")} tooltip="Align Right">
                <TextAlignRightIcon className="w-4 h-4" />
              </ToolbarButton>
              <div className="w-px h-6 bg-gray-300 mx-1" />
              <ToolbarButton onClick={handleDelete} tooltip="Delete Image">
                <TrashIcon className="w-4 h-4 text-red-600 hover:text-red-700" />
              </ToolbarButton>
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        imageEl
      )}
    </NodeViewWrapper>
  );
};
